import { getLocale, LOCALE_EN, LOCALE_ZH, onLocaleChange, translateText } from './index'

const TRANSLATABLE_ATTRIBUTES = ['placeholder', 'title', 'aria-label', 'alt']
const textSourceMap = new WeakMap()
const attrSourceMap = new WeakMap()

const shouldSkipTextNode = (node) => {
  const parent = node?.parentElement
  if (!parent) return true
  const tag = parent.tagName
  return tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'CODE' || tag === 'PRE'
}

const translateTextNode = (node, locale) => {
  if (shouldSkipTextNode(node)) return
  const currentText = node.nodeValue
  if (!currentText || !currentText.trim()) return

  if (!textSourceMap.has(node)) {
    textSourceMap.set(node, currentText)
  }

  const sourceText = textSourceMap.get(node)
  const nextText = locale === LOCALE_EN ? translateText(sourceText, locale) : sourceText
  if (node.nodeValue !== nextText) {
    node.nodeValue = nextText
  }
}

const translateElementAttributes = (element, locale) => {
  if (!element || element.nodeType !== Node.ELEMENT_NODE) return

  let source = attrSourceMap.get(element)
  if (!source) {
    source = {}
    attrSourceMap.set(element, source)
  }

  TRANSLATABLE_ATTRIBUTES.forEach((attrName) => {
    if (!element.hasAttribute(attrName)) return
    if (source[attrName] === undefined) {
      source[attrName] = element.getAttribute(attrName)
    }
    const original = source[attrName]
    const translated = locale === LOCALE_EN ? translateText(original, locale) : original
    if (element.getAttribute(attrName) !== translated) {
      element.setAttribute(attrName, translated)
    }
  })
}

const translateDomTree = (root, locale) => {
  if (!root) return

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let current = walker.nextNode()
  while (current) {
    translateTextNode(current, locale)
    current = walker.nextNode()
  }

  if (root.nodeType === Node.ELEMENT_NODE) {
    translateElementAttributes(root, locale)
    root.querySelectorAll('*').forEach((element) => {
      translateElementAttributes(element, locale)
    })
  }
}

export const createDomI18nController = (rootProvider) => {
  let observer = null
  let rafId = 0
  let applying = false

  const resolveRoot = () => (typeof rootProvider === 'function' ? rootProvider() : rootProvider)

  const apply = () => {
    if (applying) return
    const root = resolveRoot()
    if (!root) return

    applying = true
    try {
      const locale = getLocale()
      translateDomTree(root, locale)
      document.documentElement.setAttribute('lang', locale === LOCALE_EN ? LOCALE_EN : LOCALE_ZH)
    } finally {
      applying = false
      rafId = 0
    }
  }

  const scheduleApply = () => {
    if (rafId) {
      cancelAnimationFrame(rafId)
    }
    rafId = requestAnimationFrame(apply)
  }

  const stopLocaleWatch = onLocaleChange(() => {
    scheduleApply()
  })

  const start = () => {
    const root = resolveRoot()
    if (!root) return

    observer = new MutationObserver(() => {
      if (!applying) {
        scheduleApply()
      }
    })

    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: TRANSLATABLE_ATTRIBUTES
    })

    scheduleApply()
  }

  const stop = () => {
    if (rafId) {
      cancelAnimationFrame(rafId)
      rafId = 0
    }
    if (observer) {
      observer.disconnect()
      observer = null
    }
    stopLocaleWatch()
  }

  return {
    start,
    stop,
    refresh: scheduleApply
  }
}

